import React from 'react';
import { X } from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';

/**
 * AdminListModal — read-only drill-down for a clicked admin dashboard stat.
 * Matches the app's existing custom-overlay modal convention (see
 * EditBookingModal) rather than the shadcn Dialog primitive, for visual
 * consistency with the rest of the dashboard.
 *
 * Props:
 *   title       — modal heading, e.g. "Total Users (6)"
 *   columns     — [{ key, label }]
 *   rows        — array of plain objects, one per record
 *   loading     — boolean
 *   emptyLabel  — text shown when rows is empty and not loading
 *   onRowClick(row) — optional; when provided, rows become clickable
 *   onClose()
 */
const AdminListModal = ({ title, columns, rows, loading, emptyLabel, onRowClick, onClose }) => {
    return (
        <div
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={onClose}
            data-testid="admin-list-modal-overlay"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] shadow-2xl p-6 flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-list-modal"
            >
                <div className="flex items-center justify-between mb-4 shrink-0">
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="admin-list-modal-title">
                        {title}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="admin-list-modal-close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="py-10 text-center text-slate-400 text-sm" data-testid="admin-list-modal-loading">Loading…</div>
                    ) : rows.length === 0 ? (
                        <div className="py-10 text-center text-slate-400 text-sm" data-testid="admin-list-modal-empty">{emptyLabel}</div>
                    ) : (
                        <Table data-testid="admin-list-modal-table">
                            <TableHeader>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableHead key={col.key}>{col.label}</TableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((row, i) => (
                                    <TableRow
                                        key={row.id || i}
                                        data-testid={`admin-list-modal-row-${i}`}
                                        onClick={onRowClick ? () => onRowClick(row) : undefined}
                                        className={onRowClick ? 'cursor-pointer hover:bg-purple-50/60' : undefined}
                                    >
                                        {columns.map((col) => (
                                            <TableCell key={col.key}>{col.render ? col.render(row) : (row[col.key] ?? '—')}</TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminListModal;
